import { DeviceTypeConfigurations } from "../../Configurations/DeviceTypeConfiguration/DeviceTypeConfigurations.js";
import type { ThirdPartyDeviceIdentifierScheme } from "../../Configurations/DeviceTypeConfiguration/types.js";
import type { ThirdPartyDeviceType } from "../../DeviceTypes/ThirdPartyDevice.js";
import { toLoraEui } from "../../utils/shortCodes/EuiShortCodeMapper.js";
import { loraEuiRegex } from "../assetIdentity/assetIdentity.js";

/**
 * Lowercase 8-4-4-4-12 UUID, e.g. "b3536c25-6e6e-4f9e-9d1a-6f0b6f0b6f0b" — the one canonical form every
 * UUID version shares, so no per-version variant is needed.
 */
export const UUID_FORMAT = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

/** Vendor-defined identifiers carry no assumed shape beyond being present. */
export const VENDOR_OPAQUE_ID_FORMAT = /^.+$/;

/**
 * Per-scheme canonical shape, matched against the already-normalized node_id (see `normalizeNodeId`).
 * Only `loraEui` gets case leniency, applied by callers at the Zod layer; mongoose validates strictly.
 */
const nodeIdRegexByScheme: Record<ThirdPartyDeviceIdentifierScheme, RegExp> = {
    loraEui: loraEuiRegex,
    uuid: UUID_FORMAT,
    vendorOpaque: VENDOR_OPAQUE_ID_FORMAT,
};

/**
 * Resolves a device type's identifier scheme. An unconfigured/unknown device type defaults to `loraEui`,
 * matching the historical behaviour of validating every node_id against a LoRa EUI regardless of
 * deviceType — a device type outside `DeviceTypeConfigurations` (e.g. a stale/legacy value) must keep
 * validating exactly as before, not start rejecting every node_id.
 */
export function nodeIdSchemeForDeviceType(deviceType: ThirdPartyDeviceType): ThirdPartyDeviceIdentifierScheme {
    return DeviceTypeConfigurations[deviceType]?.identifierScheme ?? "loraEui";
}

/**
 * Normalizes `node_id` the way its device type's scheme expects before validating it. Only `loraEui` coerces
 * case today; every other scheme is asserted as-is.
 */
export function normalizeNodeId(deviceType: ThirdPartyDeviceType, node_id: string): string {
    return nodeIdSchemeForDeviceType(deviceType) === "loraEui" ? node_id.toLowerCase() : node_id;
}

/** True when `node_id` already satisfies its device type's identifier scheme (case included). */
export function isValidNodeId(deviceType: ThirdPartyDeviceType, node_id: string): boolean {
    return nodeIdRegexByScheme[nodeIdSchemeForDeviceType(deviceType)].test(node_id);
}

/** Human-readable reason `node_id` failed validation, for reuse across the mongoose and Zod layers. */
export function nodeIdValidationMessage(deviceType: ThirdPartyDeviceType, node_id: string): string {
    const scheme = nodeIdSchemeForDeviceType(deviceType);
    return `node_id "${node_id}" does not match the "${scheme}" identifier scheme required by device type "${deviceType}"`;
}

/**
 * The `loraEui` to sync onto `AssetIdentity` for this device, or `undefined` when the device type's
 * identifier scheme isn't `loraEui` — those devices sync on `verdiId` alone (see `assetIdentitySync`).
 */
export function loraEuiForAssetIdentitySync(
    deviceType: ThirdPartyDeviceType,
    node_id: string | undefined,
): string | undefined {
    if (!node_id || nodeIdSchemeForDeviceType(deviceType) !== "loraEui") return undefined;
    // Preserves the historical behaviour byte-for-byte: toLoraEui (not normalizeNodeId) is what the
    // AssetIdentity sync has always derived loraEui from.
    return toLoraEui(node_id);
}
